import { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import FadeIn from '@/components/FadeIn';
import PrivacyPolicyOverlay from '@/components/PrivacyPolicyOverlay';
import CookiePreferencesModal from '@/components/CookiePreferencesModal';
import { FileText, Settings, Lightbulb, Scale, ReceiptText, Cookie } from 'lucide-react';
import { unsolicitedPolicy } from '@/data/unsolicitedPolicy';
import { termsOfUse } from '@/data/termsOfUse';
import { refundPolicy } from '@/data/refundPolicy';
import { cookiePolicy } from '@/data/cookiePolicy';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

const LOGO_AVIF = 'https://customer-assets.emergentagent.com/job_3ec12bb3-915e-4bbb-87b2-e085aa4a2c01/artifacts/jfotexpw_Logo%20Wee%20V2%20-%202026%20-%20SBG.HQ.avif';
const LOGO_WEBP = 'https://customer-assets.emergentagent.com/job_3ec12bb3-915e-4bbb-87b2-e085aa4a2c01/artifacts/76ox2huh_Logo%20Wee%20V2%20-%202026%20-%20SBG.HQ.webp';

function PolicyModal({ open, onClose, data, language, testId, accentColor, prefix }) {
  const doc = data[language] || data.pt;
  const scrollRef = useRef(null);

  const scrollToSection = (id) => {
    const container = scrollRef.current;
    if (!container) return;
    const viewport = container.querySelector('[data-radix-scroll-area-viewport]');
    const target = viewport?.querySelector(`#${prefix}-${id}`);
    if (target && viewport) {
      viewport.scrollTo({ top: target.offsetTop - viewport.offsetTop - 16, behavior: 'smooth' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid={testId} className="bg-[#F9F8F6] border-[#333333]/10 max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-xl font-light tracking-tight" style={{ color: accentColor }}>
            {doc.title}
          </DialogTitle>
          <DialogDescription className="sr-only">{doc.version}</DialogDescription>
          <p className="text-xs font-light text-[#333333]/50 mt-1">{doc.lastUpdated}</p>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] pr-4" ref={scrollRef}>
          <div className="space-y-6 py-4">
            {doc.introduction && (
              <div className="text-sm font-light text-[#333333]/90 leading-relaxed whitespace-pre-line">
                {doc.introduction}
              </div>
            )}

            {doc.tocTitle && (
              <div>
                <h3 className="text-base font-normal text-[#333333] mb-3">{doc.tocTitle}</h3>
                <nav className="flex flex-col gap-1.5">
                  {doc.sections.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => scrollToSection(s.id)}
                      className="text-sm font-light hover:underline text-left transition-colors duration-200"
                      style={{ color: accentColor }}
                      data-testid={`toc-${prefix}-${s.id}`}
                    >
                      {s.title}
                    </button>
                  ))}
                </nav>
              </div>
            )}

            <div className="w-full h-[1px] bg-[#333333]/10" />

            {doc.sections.map((s) => (
              <div key={s.id} id={`${prefix}-${s.id}`}>
                <h3 className="text-sm font-normal text-[#333333] mb-1.5">{s.title}</h3>
                <p className="text-xs font-light text-[#333333]/90 leading-relaxed whitespace-pre-line">{s.content}</p>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

function TermsOfUseModal({ open, onClose, language }) {
  const terms = termsOfUse[language] || termsOfUse.pt;
  const scrollRef = useRef(null);

  const scrollToSection = (id) => {
    const container = scrollRef.current;
    if (!container) return;
    const viewport = container.querySelector('[data-radix-scroll-area-viewport]');
    const target = viewport?.querySelector(`#terms-${id}`);
    if (target && viewport) {
      const offset = target.offsetTop - viewport.offsetTop;
      viewport.scrollTo({ top: offset - 16, behavior: 'smooth' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="terms-of-use-modal" className="bg-[#F9F8F6] border-[#333333]/10 max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-xl font-light text-[#9B87C2] tracking-tight">
            {terms.title}
          </DialogTitle>
          <DialogDescription className="sr-only">{terms.version}</DialogDescription>
          <p className="text-xs font-light text-[#333333]/50 mt-1">
            {terms.lastUpdated}
          </p>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] pr-4" ref={scrollRef}>
          <div className="space-y-6 py-4">
            <div className="text-sm font-light text-[#333333]/90 leading-relaxed whitespace-pre-line">
              {terms.introduction}
            </div>

            {terms.tocTitle && (
              <div>
                <h3 className="text-base font-normal text-[#333333] mb-3">
                  {terms.tocTitle}
                </h3>
                <nav className="flex flex-col gap-1.5">
                  {terms.sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className="text-sm font-light text-[#9B87C2] hover:underline text-left transition-colors duration-200"
                      data-testid={`toc-terms-${section.id}`}
                    >
                      {section.title}
                    </button>
                  ))}
                </nav>
              </div>
            )}

            <div className="w-full h-[1px] bg-[#333333]/10" />

            {terms.sections.map((section) => (
              <div key={section.id} id={`terms-${section.id}`}>
                <h3 className="text-sm font-normal text-[#333333] mb-1.5">{section.title}</h3>
                <p className="text-xs font-light text-[#333333]/90 leading-relaxed whitespace-pre-line">{section.content}</p>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default function PrivacyPage() {
  const { language, t } = useLanguage();
  const [policyOpen, setPolicyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [unsolicitedOpen, setUnsolicitedOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [cookiePolicyOpen, setCookiePolicyOpen] = useState(false);
  const { scrollYProgress } = useScroll();
  const ringRotation = useTransform(scrollYProgress, [0, 1], [0, 360]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#F9F8F6] min-h-screen" data-testid="privacy-page">
      <Header />

      {/* Right pane - Fixed logo (desktop only, mirrored layout) */}
      <div className="hidden md:flex fixed right-0 top-0 w-1/2 h-screen items-center justify-center bg-[#F9F8F6] z-10">
        <svg
          className="absolute w-[600px] h-[600px] opacity-[0.03]"
          viewBox="0 0 600 600"
        >
          <ellipse cx="300" cy="300" rx="280" ry="260" fill="#C2A8F7" />
        </svg>
        <div className="relative w-[85%] max-w-[700px] aspect-square">
          <picture>
            <source srcSet={LOGO_AVIF} type="image/avif" />
            <source srcSet={LOGO_WEBP} type="image/webp" />
            <img
              src={LOGO_WEBP}
              alt={t.slogan.logoAlt}
              loading="lazy"
              decoding="async"
              width={1200}
              height={1200}
              className="w-full h-full object-contain relative z-10"
            />
          </picture>
          <motion.div
            className="absolute inset-[-8%] rounded-full border border-[#35589B]/[0.07] z-0"
            style={{ rotate: ringRotation }}
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#35589B]/20" />
          </motion.div>
        </div>
      </div>

      {/* Left pane - Content */}
      <main className="relative w-full md:w-1/2">
        <section className="min-h-screen flex flex-col justify-center px-8 md:px-16 lg:px-24 py-24">
          {/* Mobile logo */}
          <div className="md:hidden flex justify-center mb-12">
            <div className="w-72 aspect-square">
              <picture>
                <source srcSet={LOGO_AVIF} type="image/avif" />
                <source srcSet={LOGO_WEBP} type="image/webp" />
                <img src={LOGO_WEBP} alt={t.slogan.logoAlt} loading="lazy" decoding="async" width={1200} height={1200} className="w-full h-full object-contain" />
              </picture>
            </div>
          </div>

          <FadeIn>
            <h1
              data-testid="privacy-title"
              className="text-3xl md:text-4xl lg:text-5xl font-light text-[#47A2A2] tracking-tight leading-tight mb-6"
            >
              {t.privacy.title}
            </h1>
          </FadeIn>

          <FadeIn delay={0.2}>
            <p
              data-testid="privacy-subtitle"
              className="text-base md:text-lg font-light text-[#333333]/90 leading-relaxed max-w-lg mb-16"
            >
              {t.privacy.subtitle}
            </p>
          </FadeIn>

          <div className="flex flex-col gap-8">
            <FadeIn delay={0.4}>
              <button
                data-testid="privacy-policy-link"
                onClick={() => setPolicyOpen(true)}
                className="group flex items-center gap-4 text-left"
              >
                <div className="w-12 h-12 rounded-xl bg-[#35589B]/10 flex items-center justify-center group-hover:bg-[#35589B]/20 transition-colors duration-500">
                  <FileText size={20} className="text-[#35589B]" />
                </div>
                <span
                  className="text-base font-light tracking-wider group-hover:underline transition-all duration-300"
                  style={{ color: '#35589B' }}
                >
                  {t.privacy.policyLink}
                </span>
              </button>
            </FadeIn>

            <FadeIn delay={0.43}>
              <button
                data-testid="privacy-terms-link"
                onClick={() => setTermsOpen(true)}
                className="group flex items-center gap-4 text-left"
              >
                <div className="w-12 h-12 rounded-xl bg-[#C2A8F7]/10 flex items-center justify-center group-hover:bg-[#C2A8F7]/20 transition-colors duration-500">
                  <Scale size={20} className="text-[#C2A8F7]" />
                </div>
                <span
                  className="text-base font-light tracking-wider group-hover:underline transition-all duration-300"
                  style={{ color: '#9B87C2' }}
                >
                  {t.privacy.termsLink}
                </span>
              </button>
            </FadeIn>

            <FadeIn delay={0.46}>
              <button
                data-testid="privacy-unsolicited-link"
                onClick={() => setUnsolicitedOpen(true)}
                className="group flex items-center gap-4 text-left"
              >
                <div className="w-12 h-12 rounded-xl bg-[#47A2A2]/10 flex items-center justify-center group-hover:bg-[#47A2A2]/20 transition-colors duration-500">
                  <Lightbulb size={20} className="text-[#47A2A2]" />
                </div>
                <span
                  className="text-base font-light tracking-wider group-hover:underline transition-all duration-300"
                  style={{ color: '#47A2A2' }}
                >
                  {t.privacy.unsolicitedLink}
                </span>
              </button>
            </FadeIn>

            <FadeIn delay={0.49}>
              <button
                data-testid="privacy-refund-link"
                onClick={() => setRefundOpen(true)}
                className="group flex items-center gap-4 text-left"
              >
                <div className="w-12 h-12 rounded-xl bg-[#D4956A]/10 flex items-center justify-center group-hover:bg-[#D4956A]/20 transition-colors duration-500">
                  <ReceiptText size={20} className="text-[#D4956A]" />
                </div>
                <span
                  className="text-base font-light tracking-wider group-hover:underline transition-all duration-300"
                  style={{ color: '#D4956A' }}
                >
                  {t.privacy.refundLink}
                </span>
              </button>
            </FadeIn>

            <FadeIn delay={0.52}>
              <button
                data-testid="privacy-cookie-policy-link"
                onClick={() => setCookiePolicyOpen(true)}
                className="group flex items-center gap-4 text-left"
              >
                <div className="w-12 h-12 rounded-xl bg-[#6B8E7B]/10 flex items-center justify-center group-hover:bg-[#6B8E7B]/20 transition-colors duration-500">
                  <Cookie size={20} className="text-[#6B8E7B]" />
                </div>
                <span
                  className="text-base font-light tracking-wider group-hover:underline transition-all duration-300"
                  style={{ color: '#6B8E7B' }}
                >
                  {t.privacy.cookiePolicyLink}
                </span>
              </button>
            </FadeIn>

            <FadeIn delay={0.55}>
              <button
                data-testid="privacy-manage-prefs"
                onClick={() => setPrefsOpen(true)}
                className="group flex items-center gap-4 text-left"
              >
                <div className="w-12 h-12 rounded-xl bg-[#873D77]/10 flex items-center justify-center group-hover:bg-[#873D77]/20 transition-colors duration-500">
                  <Settings size={20} className="text-[#873D77]" />
                </div>
                <span
                  className="text-base font-light tracking-wider group-hover:underline transition-all duration-300"
                  style={{ color: '#873D77' }}
                >
                  {t.privacy.preferencesLink}
                </span>
              </button>
            </FadeIn>
          </div>
        </section>

        <Footer />
      </main>

      {/* Overlays */}
      <PrivacyPolicyOverlay open={policyOpen} onClose={() => setPolicyOpen(false)} />
      <CookiePreferencesModal open={prefsOpen} onClose={() => setPrefsOpen(false)} />

      {/* Terms of Use Read-Only Modal */}
      <TermsOfUseModal open={termsOpen} onClose={() => setTermsOpen(false)} language={language} />

      {/* Unsolicited Policy Read-Only Modal */}
      <PolicyModal
        open={unsolicitedOpen}
        onClose={() => setUnsolicitedOpen(false)}
        data={unsolicitedPolicy}
        language={language}
        testId="unsolicited-readonly-modal"
        accentColor="#47A2A2"
        prefix="unsolicited"
      />

      {/* Refund Policy Modal */}
      <PolicyModal
        open={refundOpen}
        onClose={() => setRefundOpen(false)}
        data={refundPolicy}
        language={language}
        testId="refund-policy-modal"
        accentColor="#D4956A"
        prefix="refund"
      />

      {/* Cookie Policy Modal */}
      <PolicyModal
        open={cookiePolicyOpen}
        onClose={() => setCookiePolicyOpen(false)}
        data={cookiePolicy}
        language={language}
        testId="cookie-policy-modal"
        accentColor="#6B8E7B"
        prefix="cookie"
      />
    </div>
  );
}
